import { ReactNode, useEffect, useState } from "react";

import { OrganizationContext } from "@/contexts/organization-context";
import { useAuthContext } from "@/hooks/use-auth-context";
import { OrganizationWithRole, useGetOrganizations } from "@/services/organization";
import {
  clearCurrentOrganizationId,
  readCurrentOrganizationId,
  resolveOrganizationSelection,
  storeCurrentOrganizationId,
} from "@/utils/organization-selection";

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuthContext();
  const userId = user?.id || null;
  const {
    organizations,
    loading: organizationsLoading,
    refresh: refreshOrganizations,
  } = useGetOrganizations(!!userId);
  const [currentOrganization, setCurrentOrganizationState] =
    useState<OrganizationWithRole | null>(null);
  const [initializedUserId, setInitializedUserId] = useState<string | null>(null);

  useEffect(() => {
    const selection = resolveOrganizationSelection({
      authLoading,
      organizationsLoading,
      userId,
      organizations,
      storedOrganizationId: readCurrentOrganizationId(),
    });

    if (selection.status === "wait") return;

    if (selection.status === "clear") {
      setCurrentOrganizationState(null);
      setInitializedUserId(null);
      clearCurrentOrganizationId();
      return;
    }

    const selectedOrganization = selection.organization;

    setCurrentOrganizationState(selectedOrganization);
    setInitializedUserId(userId);

    if (selectedOrganization) {
      storeCurrentOrganizationId(selectedOrganization.id);
    } else {
      clearCurrentOrganizationId();
    }
  }, [authLoading, organizations, organizationsLoading, userId]);

  const setCurrentOrganization = (organization: OrganizationWithRole | null) => {
    setCurrentOrganizationState(organization);
    if (organization) {
      storeCurrentOrganizationId(organization.id);
    } else {
      clearCurrentOrganizationId();
    }
  };

  return (
    <OrganizationContext.Provider
      value={{
        currentOrganization,
        organizations,
        loading:
          authLoading ||
          organizationsLoading ||
          (!!userId && initializedUserId !== userId),
        setCurrentOrganization,
        refreshOrganizations,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}
